import { MenuList as MuiMenuList, type MenuListProps } from "@mui/material";

export type { MenuListProps };

/**
 * An adapter, not a pass-through: every menu surface in the app wants the same three departures from
 * MUI's defaults, and each one fails *silently* when it is left off.
 *
 * - `variant="menu"` — MUI defaults to `selectedMenu`, which aligns initial focus on the selected row.
 *   Our menus have no selected row to align to, and the items join the roving tab index through
 *   `MenuListContext` either way, so `menu` is the variant that matches how these are opened.
 * - `disablePadding` — `List` otherwise writes its 8px as padding *longhands*, which outrank any
 *   `padding` a panel sets from `sx`. Forgetting this is what once made every row 12px taller.
 * - `component="div"` — a `<ul>` cannot hold the `div` that wraps a disabled item's tooltip. The root
 *   carries a hardcoded `role="menu"` regardless of the tag, so nothing is lost from the a11y tree.
 *
 * All are plain defaults; pass the prop to opt back into MUI's behaviour.
 */
export const MenuList = ({
  component = "div",
  variant = "menu",
  disablePadding = true,
  ...props
}: MenuListProps) => (
  <MuiMenuList component={component} variant={variant} disablePadding={disablePadding} {...props} />
);
